import { ResponsiveTable, useTranslation } from '@hbs-us/ui';

import type { MoneyFormatters } from '../../_money/useMoneyFormatters.js';
import type { EstimateLineItemDto } from '../contractsMirror.js';

/**
 * Read-only estimate line-items table (legacy `EstimateDetailPage` Line Items).
 * A real semantic `<table>` — Description · Quantity · Unit · **Base Cost** ·
 * **Upcharge** · **Sell Unit Price** · **Line Total** — with `<th scope="col">`
 * headers, numeric columns right-aligned, mono numerals, and a readable colspan
 * empty row (A11Y-01). Every color is a locked token.
 *
 * The estimate line-item shape differs from the invoice/shared `LineItemsTable`
 * 5-col shape (it carries Base Cost + Upcharge %), so it has its own column set
 * here while still reusing the shared money formatter (`fmt`).
 *
 * `ResponsiveTable` reflows the seven columns to label:value cards below `md` —
 * seven money columns are unreadable on a phone as a row. `md`+ is unchanged.
 */

const TEXT_TH =
  'px-3 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide';
const NUMERIC_TH =
  'px-3 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide';
const NUMERIC_TD = 'px-3 py-2 text-right font-mono text-foreground text-sm';

function Dash() {
  return <span className="text-muted-foreground">—</span>;
}

export function EstimateLineItemsTable({
  rows,
  fmt,
}: {
  rows: EstimateLineItemDto[];
  fmt: MoneyFormatters;
}) {
  const { t } = useTranslation();

  return (
    <ResponsiveTable>
      <table className="w-full border-collapse">
        <caption className="sr-only">{t('estimates:lineItems.caption')}</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className={TEXT_TH}>
              {t('estimates:lineItems.description')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('estimates:lineItems.quantity')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('estimates:lineItems.unit')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('estimates:lineItems.baseCost')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('estimates:lineItems.upcharge')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('estimates:lineItems.sellUnitPrice')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('estimates:lineItems.lineTotal')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground text-sm">
                {t('estimates:detail.noLineItems')}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id} className="border-border/60 border-b last:border-b-0">
                <td className="px-3 py-2 text-foreground text-sm">{row.description || <Dash />}</td>
                <td className={NUMERIC_TD}>{row.quantity || <Dash />}</td>
                <td className="px-3 py-2 text-foreground text-sm">{row.unit || <Dash />}</td>
                <td className={NUMERIC_TD}>{fmt.formatCurrency(row.baseCost || 0)}</td>
                <td className={NUMERIC_TD}>{fmt.percent(row.upchargePercent || 0)}</td>
                <td className={NUMERIC_TD}>{fmt.formatCurrency(row.sellUnitPrice || 0)}</td>
                <td className={`${NUMERIC_TD} font-medium`}>
                  {fmt.formatCurrency(row.lineTotal || 0)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </ResponsiveTable>
  );
}
